import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Modal } from '../ui';
import { useAuthStore } from '../../store/authStore';

interface CleanupSuggestion {
  contact_id: string;
  current: Record<string, string | null>;
  suggested: Record<string, string | null>;
  changes: string[];
  confidence: 'high' | 'medium' | 'low';
}

interface CleanupModalProps {
  open: boolean;
  onClose: () => void;
}

type CleanupStep = 'loading' | 'reviewing' | 'complete';

export function CleanupModal({ open, onClose }: CleanupModalProps) {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const apiUrl = import.meta.env.VITE_API_URL;

  const [step, setStep] = useState<CleanupStep>('loading');
  const [suggestions, setSuggestions] = useState<CleanupSuggestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [applied, setApplied] = useState(0);
  const [skipped, setSkipped] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Load suggestions when modal opens
  useEffect(() => {
    if (open) {
      loadSuggestions();
    }
  }, [open]);

  const loadSuggestions = async () => {
    setStep('loading');
    setError(null);

    try {
      const response = await fetch(`${apiUrl}/contacts/cleanup/analyze`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ limit: 50 }),
      });

      const data = await response.json();

      if (data.success && data.data.suggestions.length > 0) {
        setSuggestions(data.data.suggestions);
        setCurrentIndex(0);
        setApplied(0);
        setSkipped(0);
        setStep('reviewing');
      } else if (data.success && data.data.suggestions.length === 0) {
        // No cleanup needed
        setSuggestions([]);
        setStep('complete');
      } else {
        setError(data.error?.message || 'Failed to analyze contacts');
        setStep('complete');
      }
    } catch (err) {
      setError('Failed to connect to AI service');
      setStep('complete');
    }
  };

  // Apply changes mutation
  const applyMutation = useMutation({
    mutationFn: async (suggestion: CleanupSuggestion) => {
      const response = await fetch(`${apiUrl}/contacts/${suggestion.contact_id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(suggestion.suggested),
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error?.message);
      return data;
    },
    onSuccess: () => {
      setApplied(prev => prev + 1);
      goToNext();
    },
    onError: () => {
      // Still move forward on error
      goToNext();
    },
  });

  const goToNext = () => {
    if (currentIndex < suggestions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setStep('complete');
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    }
  };

  const handleApply = () => {
    const current = suggestions[currentIndex];
    if (current) {
      applyMutation.mutate(current);
    }
  };

  const handleSkip = () => {
    setSkipped(prev => prev + 1);
    goToNext();
  };

  const handleClose = () => {
    setStep('loading');
    setSuggestions([]);
    setCurrentIndex(0);
    setApplied(0);
    setSkipped(0);
    setError(null);
    onClose();
  };

  const current = suggestions[currentIndex];

  // Get human-readable label for change type
  const getChangeLabel = (change: string) => {
    const labels: Record<string, string> = {
      split_name: 'Split name',
      format_phone: 'Format phone',
      format_address: 'Format address',
      capitalize: 'Fix capitalization',
      suggest_stage: 'Suggest stage',
      fix_typo: 'Fix typo',
    };
    return labels[change] || change;
  };

  // Get field label
  const getFieldLabel = (field: string) => {
    const labels: Record<string, string> = {
      first_name: 'First Name',
      last_name: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      phone_secondary: 'Phone (Secondary)',
      company: 'Company',
      address_street: 'Street',
      address_city: 'City',
      address_state: 'State',
      address_zip: 'ZIP',
      stage: 'Stage',
      notes: 'Notes',
    };
    return labels[field] || field;
  };

  // Get confidence color
  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
      case 'medium':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30';
      case 'low':
        return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900/30';
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="AI Contact Cleanup"
      size="lg"
    >
      <div className="px-6 pb-6">
        {/* Loading */}
        {step === 'loading' && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              AI is analyzing your contacts...
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Looking for improvements
            </p>
          </div>
        )}

        {/* Reviewing */}
        {step === 'reviewing' && current && (
          <div>
            {/* Progress */}
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                <span>Reviewing contact {currentIndex + 1} of {suggestions.length}</span>
                <span>{applied} applied, {skipped} skipped</span>
              </div>
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-500 transition-all duration-300"
                  style={{ width: `${((currentIndex + 1) / suggestions.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Contact Card */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              {/* Header */}
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {current.suggested.first_name || current.current.first_name || 'Unknown'}{' '}
                  {current.suggested.last_name || current.current.last_name || ''}
                </h3>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${getConfidenceColor(current.confidence)}`}>
                  {current.confidence} confidence
                </span>
              </div>

              {/* Changes */}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Changes:</span>
                  <div className="flex flex-wrap gap-1">
                    {current.changes.map((change, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded"
                      >
                        {getChangeLabel(change)}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Comparison Table */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Field
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Current
                        </th>
                        <th className="px-4 py-2 text-center text-gray-400">
                          →
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-purple-600 dark:text-purple-400 uppercase">
                          Suggested
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {Object.keys(current.suggested).map((field) => {
                        const currentVal = current.current[field];
                        const suggestedVal = current.suggested[field];
                        const isChanged = currentVal !== suggestedVal;

                        if (!isChanged && !suggestedVal) return null;

                        return (
                          <tr key={field} className={isChanged ? 'bg-purple-50/50 dark:bg-purple-900/10' : ''}>
                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                              {getFieldLabel(field)}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                              {currentVal || <span className="text-gray-400 italic">empty</span>}
                            </td>
                            <td className="px-4 py-2 text-center text-gray-400">
                              {isChanged ? '→' : ''}
                            </td>
                            <td className={`px-4 py-2 text-sm ${isChanged ? 'text-purple-700 dark:text-purple-300 font-medium' : 'text-gray-900 dark:text-white'}`}>
                              {suggestedVal || <span className="text-gray-400 italic">empty</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={handleSkip}
                className="flex-1"
                disabled={applyMutation.isPending}
              >
                Skip
              </Button>
              <Button
                onClick={handleApply}
                className="flex-1"
                disabled={applyMutation.isPending}
              >
                {applyMutation.isPending ? 'Applying...' : 'Apply Changes'}
              </Button>
            </div>
          </div>
        )}

        {/* Complete */}
        {step === 'complete' && (
          <div className="text-center py-8">
            {error ? (
              <>
                <div className="text-5xl mb-4">⚠️</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Cleanup Unavailable
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {error}
                </p>
              </>
            ) : suggestions.length === 0 ? (
              <>
                <div className="text-5xl mb-4">✨</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Contacts Look Great!
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  AI didn't find any obvious improvements needed.
                </p>
              </>
            ) : (
              <>
                <div className="text-5xl mb-4">🎉</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Cleanup Complete
                </h3>
                <div className="flex justify-center gap-6 text-lg mb-6">
                  <div className="text-green-600 dark:text-green-400">
                    <span className="font-bold">{applied}</span> updated
                  </div>
                  <div className="text-gray-500 dark:text-gray-400">
                    <span className="font-bold">{skipped}</span> skipped
                  </div>
                </div>
              </>
            )}

            <Button onClick={handleClose}>
              Done
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
